import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Check, Landmark, Pencil, Plus, Star, Trash2, X } from "lucide-react";

import { Button } from "components/ui/Button";
import { ConfirmationOverlay } from "components/feedback/ConfirmationOverlay/ConfirmationOverlay";
import { CardsSection } from "components/layout/Section/CardsSection";
import { IbanInput } from "components/forms/IbanInput";
import { IBAN_INVALID_MESSAGE, isValidIban } from "shared/utils/iban";
import {
  CompteBancaireDto,
  createCompteBancaire,
  deleteCompteBancaire,
  getComptesBancairesByPersonne,
  updateCompteBancaire,
} from "../api/comptesBancairesApi";

interface CorporateBankAccountsManagerProps {
  /** = personneMoraleId de la corporate (la Personne partage son Guid). */
  personId: string;
}

interface DraftAccount {
  iban: string;
  bic: string;
  label: string;
  isDefault: boolean;
}

const EMPTY_DRAFT: DraftAccount = { iban: '', bic: '', label: '', isDefault: false };

const inputClasses =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

/**
 * CRUD en place des comptes bancaires de la corporate (destination des
 * commissions dans les tunnels). Endpoints /backoffice/comptes existants.
 */
export const CorporateBankAccountsManager: React.FC<CorporateBankAccountsManagerProps> = ({
  personId,
}) => {
  const [rows, setRows] = useState<CompteBancaireDto[]>([]);
  const [loading, setLoading] = useState(false);
  // null = aucun éditeur ouvert ; 'new' = création ; sinon id en édition.
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<DraftAccount>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState<CompteBancaireDto | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getComptesBancairesByPersonne(personId);
      setRows(Array.isArray(result) ? result : []);
    } catch {
      toast.error('Erreur de chargement des comptes bancaires');
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setDraft(EMPTY_DRAFT);
    setEditing('new');
  };

  const openEdit = (row: CompteBancaireDto) => {
    setDraft({ iban: row.iban, bic: row.bic ?? '', label: row.label, isDefault: row.isDefault });
    setEditing(row.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const saveDraft = async () => {
    if (!draft.label.trim()) {
      toast.error('Le libellé est requis');
      return;
    }
    if (!isValidIban(draft.iban)) {
      toast.error(IBAN_INVALID_MESSAGE);
      return;
    }
    setBusy(true);
    try {
      const response =
        editing === 'new'
          ? await createCompteBancaire({
              personId,
              iban: draft.iban,
              bic: draft.bic || null,
              label: draft.label,
              isDefault: draft.isDefault,
            })
          : await updateCompteBancaire(editing as string, {
              iban: draft.iban,
              bic: draft.bic || null,
              label: draft.label,
              isDefault: draft.isDefault,
            });
      if (!response.isSuccess) {
        toast.error(response.errorMessage ?? "Échec de l'enregistrement");
        return;
      }
      toast.success('Compte enregistré');
      cancelEdit();
      await fetchRows();
    } catch {
      toast.error("Erreur lors de l'enregistrement du compte");
    } finally {
      setBusy(false);
    }
  };

  const setDefault = async (row: CompteBancaireDto) => {
    setBusy(true);
    try {
      const response = await updateCompteBancaire(row.id, {
        iban: row.iban,
        bic: row.bic || null,
        label: row.label,
        isDefault: true,
      });
      if (!response.isSuccess) {
        toast.error(response.errorMessage ?? 'Échec');
        return;
      }
      await fetchRows();
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    try {
      const response = await deleteCompteBancaire(toDelete.id);
      if (!response.isSuccess) {
        toast.error(response.errorMessage ?? 'Échec de la suppression');
        return;
      }
      toast.success('Compte supprimé');
      setToDelete(null);
      await fetchRows();
    } finally {
      setBusy(false);
    }
  };

  const renderEditor = () => (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
      <div className="grid grid-cols-6 gap-3">
        <IbanInput
          className={`${inputClasses} !h-auto col-span-4`}
          placeholder="IBAN (FR76 ...)"
          value={draft.iban}
          onChange={(v) => setDraft((d) => ({ ...d, iban: v }))}
        />
        <input
          className={`${inputClasses} col-span-2`}
          placeholder="BIC (optionnel)"
          value={draft.bic}
          onChange={(e) => setDraft((d) => ({ ...d, bic: e.target.value }))}
        />
        <input
          className={`${inputClasses} col-span-4`}
          placeholder="Libellé (ex. Compte commissions)"
          value={draft.label}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
        />
        <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={draft.isDefault}
            onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          Par défaut
        </label>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button outline type="button" onClick={cancelEdit} disabled={busy}>
          <X data-slot="icon" />
          Annuler
        </Button>
        <Button color="primary" type="button" onClick={saveDraft} disabled={busy}>
          <Check data-slot="icon" />
          Enregistrer
        </Button>
      </div>
    </div>
  );

  return (
    <CardsSection
        icon={<Landmark className="w-5 h-5 text-primary" />}
        title="Comptes bancaires"
      >
        <p className="-mt-2 text-sm text-gray-500">Destination des commissions dans la configuration des tunnels.</p>
      <div className="flex flex-col gap-3">
        {loading && <p className="text-sm text-gray-400">Chargement…</p>}

        {!loading && rows.length === 0 && editing !== 'new' && (
          <p className="text-sm text-gray-500">Aucun compte bancaire pour l'instant.</p>
        )}

        {rows.map((row) =>
          editing === row.id ? (
            <div key={row.id}>{renderEditor()}</div>
          ) : (
            <div
              key={row.id}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3"
            >
              <Landmark size={18} className="flex-shrink-0 text-gray-400" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 truncate">{row.label}</span>
                  {row.isDefault && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                      <Star size={10} className="fill-amber-500 text-amber-500" />
                      Par défaut
                    </span>
                  )}
                </div>
                <span className="font-mono text-xs text-gray-500">
                  {row.iban}
                  {row.bic ? ` · ${row.bic}` : ''}
                </span>
              </div>
              {!row.isDefault && (
                <button
                  type="button"
                  onClick={() => setDefault(row)}
                  disabled={busy}
                  title="Définir par défaut"
                  className="text-gray-300 hover:text-amber-500 transition-colors"
                >
                  <Star size={16} />
                </button>
              )}
              <button
                type="button"
                onClick={() => openEdit(row)}
                title="Modifier"
                className="text-gray-400 hover:text-primary transition-colors"
              >
                <Pencil size={16} />
              </button>
              <button
                type="button"
                onClick={() => setToDelete(row)}
                title="Supprimer"
                className="text-gray-400 hover:text-red-600 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ),
        )}

        {editing === 'new' && renderEditor()}

        {editing !== 'new' && (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 self-start rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-primary hover:text-primary transition-colors"
          >
            <Plus size={16} />
            Ajouter un compte bancaire
          </button>
        )}

        <p className="text-xs text-gray-500">
          Le RIB justificatif se joint depuis le processus de mandat ; ici on gère
          l'IBAN de versement.
        </p>
      </div>

      <ConfirmationOverlay
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Supprimer le compte bancaire"
        description={
          toDelete
            ? `Confirmer la suppression du compte « ${toDelete.label} » (${toDelete.iban}) ?`
            : ''
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
      />
    </CardsSection>
  );
};
